import React, { useState, useEffect } from 'react';
import { toDisplayFormat } from '../../utlis/dateUtils';
import { FiEdit, FiTrash2, FiPlus } from 'react-icons/fi';
import apiClient from '../../apiClient';
import PackageForm from '../packages/PackageForm';
import Table from '../common/Table/Table';
import '../common/Table/Table.css';

const PackageDetails = () => {
  const [packageDetails, setPackageDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentPackage, setCurrentPackage] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });

  const fetchPackageDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get('/api/packages');
      setPackageDetails(response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackageDetails();
  }, []);

  const handleEditClick = (pkg) => {
    setCurrentPackage(pkg);
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    
    try {
      await apiClient.delete(`/api/packages/${id}`);
      setMessage({ text: 'Package deleted successfully', type: 'success' });
      fetchPackageDetails();
    } catch (err) {
      setMessage({ 
        text: err.response?.data?.message || 'Failed to delete package', 
        type: 'error' 
      });
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      const url = currentPackage 
        ? `/api/packages/${currentPackage.id}`
        : '/api/packages';
      
      const method = currentPackage ? 'put' : 'post';
      
      const response = await apiClient[method](url, formData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
  
      setMessage({ 
        text: response.data.message || 
             (currentPackage ? 'Package updated' : 'Package created'),
        type: 'success' 
      });
      
      setShowForm(false);
      setCurrentPackage(null);
      fetchPackageDetails();
      
    } catch (err) {
      console.error('Form submission error:', {
        error: err,
        response: err.response
      });
      
      setMessage({
        text: err.response?.data?.message || 
             err.message || 
             'Operation failed',
        type: 'error'
      });
    }
  };

  const handleAddNew = () => {
    setCurrentPackage(null);
    setShowForm(true);
  };

  // Clear message after 5 seconds
  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  if (loading) return <div className="loading">Loading packages...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  const columns = [
    { header: 'ID' },
    { header: 'Detail' },
    { header: 'Year' },
    { header: 'Price' },
    { header: 'Updated At' },
    { header: 'Action', className: 'actions-header' }
  ];

  const renderRow = (pkg) => (
    <>
      <td>{pkg.id}</td>
      <td>{pkg.detail}</td>
      <td>{pkg.year}</td>
      <td>₹{pkg.price.toLocaleString('en-IN')}</td>
      <td>{toDisplayFormat(pkg.updated_at)}</td>
      <td className="actions-cell">
        <button 
          onClick={() => handleEditClick(pkg)}
          className="lead-edit-btn"
          title="Edit package"
        >
          <FiEdit />
        </button>
        <button 
          onClick={() => handleDeleteClick(pkg.id)}
          className="delete-btn"
          title="Delete package"
        >
          <FiTrash2 />
        </button>
      </td>
    </>
  );

  return (
    <div className="package-tab">
      <div className="package-header">
        <h2>Package Details</h2>
        <button onClick={handleAddNew} className="add-btn">
          <FiPlus /> Add New Package
        </button>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      {showForm && (
        <PackageForm
          packageData={currentPackage}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false);
            setCurrentPackage(null);
          }}
        />
      )}

      <Table 
        columns={columns} 
        data={packageDetails} 
        renderRow={renderRow} 
        className="package-table"
      />
    </div>
  );
};

export default PackageDetails;
